import { createContext, useContext, useMemo, useCallback, useEffect, useRef, ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Locale, LOCALES, translations, TranslationKey } from './translations'

const SHARED_LOCALE_COOKIE = 'rex-lang'

function readSharedLocaleCookie(): Locale | null {
  if (typeof document === 'undefined') return null
  const match = document.cookie.match(/(?:^|;\s*)rex-lang=([^;]+)/)
  if (!match) return null
  const value = decodeURIComponent(match[1])
  return (LOCALES as string[]).includes(value) ? (value as Locale) : null
}

function writeSharedLocaleCookie(locale: Locale) {
  if (typeof document === 'undefined') return
  const host = window.location.hostname
  const onRexHook = host === 'rexhook.com' || host.endsWith('.rexhook.com')
  const domainAttr = onRexHook ? '; domain=.rexhook.com' : ''
  const secureAttr = window.location.protocol === 'https:' ? '; Secure' : ''
  document.cookie = `${SHARED_LOCALE_COOKIE}=${locale}${domainAttr}; path=/; max-age=31536000; SameSite=Lax${secureAttr}`
}

interface LocaleContextValue {
  locale: Locale
  t: (key: TranslationKey) => string
  localizeHref: (path: string) => string
  stripLocale: (path: string) => string
  switchLocale: (next: Locale) => void
}

const LocaleContext = createContext<LocaleContextValue | null>(null)

export function detectLocaleFromPath(pathname: string): Locale {
  const seg = pathname.split('/').filter(Boolean)[0]
  if (seg && (LOCALES as string[]).includes(seg) && seg !== 'en') {
    return seg as Locale
  }
  return 'en'
}

export function stripLocaleFromPath(pathname: string): string {
  const parts = pathname.split('/').filter(Boolean)
  if (parts.length > 0 && (LOCALES as string[]).includes(parts[0]) && parts[0] !== 'en') {
    const rest = parts.slice(1).join('/')
    return rest ? `/${rest}` : '/'
  }
  return pathname || '/'
}

export function prefixLocale(path: string, locale: Locale): string {
  const clean = stripLocaleFromPath(path)
  if (locale === 'en') return clean
  return clean === '/' ? `/${locale}` : `/${locale}${clean}`
}

export function LocaleProvider({ children }: { children: ReactNode }) {
  const location = useLocation()
  const navigate = useNavigate()
  const didInitRedirect = useRef(false)

  const locale = useMemo(() => detectLocaleFromPath(location.pathname), [location.pathname])

  // On first mount: if the URL has no explicit locale (i.e. defaulted to 'en')
  // but a shared cookie from another rexhook.com subdomain says otherwise,
  // redirect once to match.
  useEffect(() => {
    if (didInitRedirect.current) return
    didInitRedirect.current = true
    const seg = location.pathname.split('/').filter(Boolean)[0]
    const hasUrlLocale = seg && (LOCALES as string[]).includes(seg)
    if (hasUrlLocale) return
    const cookieLocale = readSharedLocaleCookie()
    if (cookieLocale && cookieLocale !== 'en') {
      const target = prefixLocale(location.pathname, cookieLocale) + location.search + location.hash
      navigate(target, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Keep cookie in sync with the active locale so navigating across subdomains carries the preference.
  useEffect(() => {
    writeSharedLocaleCookie(locale)
  }, [locale])

  const t = useCallback(
    (key: TranslationKey) => translations[locale][key] ?? translations.en[key] ?? key,
    [locale]
  )

  const localizeHref = useCallback((path: string) => prefixLocale(path, locale), [locale])

  const stripLocale = useCallback((path: string) => stripLocaleFromPath(path), [])

  const switchLocale = useCallback(
    (next: Locale) => {
      const base = stripLocaleFromPath(location.pathname)
      const target = prefixLocale(base, next) + location.search + location.hash
      navigate(target)
      try {
        localStorage.setItem('rex.locale', next)
      } catch {
        // ignore (private mode, etc.)
      }
    },
    [location.pathname, location.search, location.hash, navigate]
  )

  const value = useMemo<LocaleContextValue>(
    () => ({ locale, t, localizeHref, stripLocale, switchLocale }),
    [locale, t, localizeHref, stripLocale, switchLocale]
  )

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
}

export function useLocale() {
  const ctx = useContext(LocaleContext)
  if (!ctx) throw new Error('useLocale must be used within LocaleProvider')
  return ctx
}
